import React, { useState, useEffect, useRef, useMemo } from 'react';
import type { Pathway, PathwayBlock } from '../../types';
import { 
  ArrowDown, 
  Plus, 
  Trash2, 
  Sparkles,
  Layers,
  ArrowRight,
  HelpCircle,
  AlertTriangle
} from 'lucide-react';
import { renderBlockIcon, getDropdownBlockTypes, getDesignCardStyle, getBlockTypeDisplayName } from '../../config/blockTypes';

interface VisualFlowCanvasProps {
  pathway: Pathway;
  onUpdate?: (blocks: PathwayBlock[]) => void;
  onEditBlock: (block: PathwayBlock) => void;
  onDeleteBlock: (blockId: string) => void;
  onAddBlock: (branchId: string, preSelected: boolean, horizontalToBlockId?: string) => void;
  onOpenSubPathway: (block: PathwayBlock) => void;
}

// Branch styling config
const BRANCH_CONFIG: Record<string, { label: string; badge: string; color: string }> = {
  STANDARD: { label: 'Standard (Novice)', badge: 'bg-blue-150 text-blue-800 border-blue-200', color: '#3b82f6' },
  ACCELERATED: { label: 'Accelerated (RPL)', badge: 'bg-purple-150 text-purple-800 border-purple-200', color: '#a855f7' },
  REMEDIAL: { label: 'Remedial (Loop)', badge: 'bg-red-150 text-red-800 border-red-200', color: '#ef4444' },
  REFRESHER: { label: 'Refresher (Renewal)', badge: 'bg-orange-150 text-orange-800 border-orange-200', color: '#f97316' },
  MERGE: { label: 'Merge', badge: 'bg-green-150 text-green-800 border-green-200', color: '#10b981' }
};

export const VisualFlowCanvas: React.FC<VisualFlowCanvasProps> = ({
  pathway,
  onUpdate,
  onEditBlock,
  onDeleteBlock,
  onAddBlock,
  onOpenSubPathway
}) => {
  // Canvas navigation states
  const [panOffset, setPanOffset] = useState({ x: 50, y: 50 });
  const [zoomScale, setZoomScale] = useState(0.95);
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0 });

  // Node drag states
  const [draggedNodeId, setDraggedNodeId] = useState<string | null>(null);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const dragPointerStartRef = useRef({ x: 0, y: 0 });
  const [localCoords, setLocalCoords] = useState<Record<string, { x: number; y: number }>>({});

  // Group blocks matching MyPathways helper
  const groupBlocks = (blocks: PathwayBlock[]): PathwayBlock[][] => {
    const groups: PathwayBlock[][] = [];
    const seen = new Set<string>();
    for (const block of blocks) {
      if (seen.has(block.id)) continue;
      if (block.horizontalGroup) {
        const group = blocks
          .filter(b => b.horizontalGroup === block.horizontalGroup)
          .sort((a, b) => (a.horizontalPosition || 0) - (b.horizontalPosition || 0));
        group.forEach(b => seen.add(b.id));
        groups.push(group);
      } else {
        seen.add(block.id);
        groups.push([block]);
      }
    }
    return groups;
  };

  // Run auto-layout if any block lacks x or y coordinates
  useEffect(() => {
    const blocks = pathway.blocks || [];
    const needsLayout = blocks.some(b => b.x === undefined || b.y === undefined);
    
    if (needsLayout && onUpdate) {
      const branches = ['STANDARD', 'ACCELERATED', 'REMEDIAL', 'REFRESHER', 'MERGE'];
      const columnSpacing = 280;
      const startX = 60;
      
      const updated = blocks.map(b => ({ ...b }));
      
      branches.forEach((branch, colIdx) => {
        const branchBlocks = updated.filter(b => b.branchId === branch || (!b.branchId && branch === 'STANDARD'));
        if (branchBlocks.length === 0) return;
        
        const colX = startX + colIdx * columnSpacing;
        const groups = groupBlocks(branchBlocks);
        
        groups.forEach((group, rowIdx) => {
          const rowY = rowIdx * 180 + 100;
          const N = group.length;
          
          group.forEach((block, idx) => {
            const originalIndex = updated.findIndex(b => b.id === block.id);
            if (originalIndex !== -1) {
              if (N === 1) {
                updated[originalIndex].x = colX;
              } else {
                updated[originalIndex].x = colX + (idx - (N - 1) / 2) * 220;
              }
              updated[originalIndex].y = rowY;
            }
          });
        });
      });
      
      onUpdate(updated);
    }
  }, [pathway.blocks, onUpdate]);

  // Handle Canvas Zoom (mouse scroll wheel)
  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    const zoomFactor = 0.05;
    const nextZoom = e.deltaY < 0 ? zoomScale + zoomFactor : zoomScale - zoomFactor;
    setZoomScale(Math.max(0.5, Math.min(1.5, nextZoom)));
  };

  // Handle Canvas Pan (drag background)
  const handlePointerDownBg = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return; // Only pan on background
    setIsPanning(true);
    panStartRef.current = { x: e.clientX - panOffset.x, y: e.clientY - panOffset.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMoveBg = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isPanning) return;
    setPanOffset({
      x: e.clientX - panStartRef.current.x,
      y: e.clientY - panStartRef.current.y
    });
  };

  const handlePointerUpBg = (e: React.PointerEvent<HTMLDivElement>) => {
    setIsPanning(false);
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  // Handle Node Dragging
  const handleNodeDragStart = (e: React.PointerEvent, blockId: string, currentX: number, currentY: number) => {
    e.stopPropagation();
    setDraggedNodeId(blockId);
    setDragStartPos({ x: currentX, y: currentY });
    dragPointerStartRef.current = { x: e.clientX, y: e.clientY };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handleNodeDragMove = (e: React.PointerEvent, blockId: string) => {
    if (draggedNodeId !== blockId) return;
    const deltaX = (e.clientX - dragPointerStartRef.current.x) / zoomScale;
    const deltaY = (e.clientY - dragPointerStartRef.current.y) / zoomScale;
    
    // Snapping to grid (10px increments)
    const nextX = Math.round((dragStartPos.x + deltaX) / 10) * 10;
    const nextY = Math.round((dragStartPos.y + deltaY) / 10) * 10;

    setLocalCoords(prev => ({
      ...prev,
      [blockId]: { x: nextX, y: nextY }
    }));
  };

  const handleNodeDragEnd = (e: React.PointerEvent, blockId: string) => {
    if (draggedNodeId !== blockId) return;
    setDraggedNodeId(null);
    e.currentTarget.releasePointerCapture(e.pointerId);

    const finalPos = localCoords[blockId];
    if (finalPos && onUpdate) {
      const updated = (pathway.blocks || []).map(b => {
        if (b.id === blockId) {
          // Detect if it was dragged to another column to update branchId
          let newBranchId = b.branchId || 'STANDARD';
          if (finalPos.x < 200) newBranchId = 'STANDARD';
          else if (finalPos.x >= 200 && finalPos.x < 480) newBranchId = 'ACCELERATED';
          else if (finalPos.x >= 480 && finalPos.x < 760) newBranchId = 'REMEDIAL';
          else if (finalPos.x >= 760 && finalPos.x < 1040) newBranchId = 'REFRESHER';
          else newBranchId = 'MERGE';
          
          return { ...b, x: finalPos.x, y: finalPos.y, branchId: newBranchId };
        }
        return b;
      });

      // Re-sort the blocks array by Y coordinate within each branch
      // to keep candidate sequential progression flow matching visual layout order!
      const sorted = [...updated].sort((a, b) => {
        if (a.branchId !== b.branchId) return 0;
        return (a.y || 0) - (b.y || 0);
      });

      onUpdate(sorted);
    }
  };

  // Render SVG links connecting nodes
  const connectionLines = useMemo(() => {
    const lines: React.ReactNode[] = [];
    const blocks = pathway.blocks || [];
    
    // Separate blocks by branch
    const branches = ['STANDARD', 'ACCELERATED', 'REMEDIAL', 'REFRESHER', 'MERGE'];
    
    branches.forEach(branch => {
      const branchBlocks = blocks
        .filter(b => b.branchId === branch || (!b.branchId && branch === 'STANDARD'))
        .map(b => ({
          ...b,
          x: localCoords[b.id]?.x ?? b.x ?? 0,
          y: localCoords[b.id]?.y ?? b.y ?? 0
        }))
        .sort((a, b) => a.y - b.y);

      const groups = groupBlocks(branchBlocks);
      
      // Draw lines between sequential groups
      for (let i = 0; i < groups.length - 1; i++) {
        const fromGroup = groups[i];
        const toGroup = groups[i + 1];
        
        fromGroup.forEach(fromNode => {
          toGroup.forEach(toNode => {
            const startX = fromNode.x + 104; // Middle of node card (w=208px)
            const startY = fromNode.y + 76;  // Bottom of node card (h=76px)
            const endX = toNode.x + 104;     // Middle of node card
            const endY = toNode.y;           // Top of node card

            const controlOffset = Math.min(100, Math.abs(endY - startY) * 0.4);
            const pathData = `M ${startX} ${startY} C ${startX} ${startY + controlOffset}, ${endX} ${endY - controlOffset}, ${endX} ${endY}`;
            
            lines.push(
              <g key={`link-${fromNode.id}-${toNode.id}`}>
                <path 
                  d={pathData} 
                  fill="none" 
                  stroke="#cbd5e1" 
                  strokeWidth="2" 
                  markerEnd="url(#arrow)"
                  className="transition-all hover:stroke-sky-400 hover:stroke-[3px] cursor-pointer"
                />
              </g>
            );
          });
        });
      }
    });

    // Draw failure / remedial loop links
    blocks.forEach(block => {
      if (block.failureBlockId) {
        const target = blocks.find(b => b.id === block.failureBlockId);
        if (target) {
          const fromX = (localCoords[block.id]?.x ?? block.x ?? 0) + 208; // Right edge
          const fromY = (localCoords[block.id]?.y ?? block.y ?? 0) + 38;  // Vertical middle
          const toX = localCoords[target.id]?.x ?? target.x ?? 0;        // Left edge
          const toY = (localCoords[target.id]?.y ?? target.y ?? 0) + 38;

          const controlX = Math.max(100, Math.abs(toX - fromX) * 0.5);
          const pathData = `M ${fromX} ${fromY} C ${fromX + controlX} ${fromY}, ${toX - controlX} ${toY}, ${toX} ${toY}`;

          lines.push(
            <g key={`fail-link-${block.id}-${target.id}`}>
              <path 
                d={pathData} 
                fill="none" 
                stroke="#ef4444" 
                strokeWidth="2" 
                strokeDasharray="4 4"
                markerEnd="url(#red-arrow)"
                className="transition-all hover:stroke-red-500 hover:stroke-[3px] cursor-pointer"
              />
              <text 
                x={(fromX + toX) / 2} 
                y={(fromY + toY) / 2 - 6} 
                textAnchor="middle" 
                className="text-[9px] font-bold fill-red-500 bg-white"
              >
                FAIL LOOP
              </text>
            </g>
          );
        }
      }
    });

    return lines;
  }, [pathway.blocks, localCoords]);

  return (
    <div className="relative overflow-hidden w-full h-[650px] border border-aero-200 rounded-xl bg-slate-50 dark:bg-slate-900 select-none shadow-inner">
      
      {/* CANVAS INNER CONTROLLER */}
      <div 
        onWheel={handleWheel}
        onPointerDown={handlePointerDownBg}
        onPointerMove={handlePointerMoveBg}
        onPointerUp={handlePointerUpBg}
        className={`w-full h-full relative cursor-grab ${isPanning ? 'cursor-grabbing' : ''}`}
      >
        <div 
          className="absolute top-0 left-0 origin-top-left"
          style={{
            transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomScale})`,
            width: '5000px',
            height: '5000px',
            backgroundImage: 'radial-gradient(circle, #cbd5e1 1.5px, transparent 1.5px)',
            backgroundSize: '24px 24px'
          }}
        >
          {/* SVG LINKS LAYER */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none z-0">
            <defs>
              <marker id="arrow" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
                <path d="M 0 1 L 10 5 L 0 9 z" fill="#cbd5e1" />
              </marker>
              <marker id="red-arrow" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
                <path d="M 0 1 L 10 5 L 0 9 z" fill="#ef4444" />
              </marker>
            </defs>
            {connectionLines}
          </svg>

          {/* COLUMN HEADER LABELS */}
          <div className="absolute top-1 flex gap-[72px] lg:gap-[72px] pointer-events-none">
            {['STANDARD', 'ACCELERATED', 'REMEDIAL', 'REFRESHER', 'MERGE'].map((bId, idx) => (
              <div 
                key={bId}
                style={{ left: idx * 280 + 60, position: 'absolute' }}
                className="w-[208px] text-center"
              >
                <span className={`inline-block px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border shadow-sm ${BRANCH_CONFIG[bId].badge}`}>
                  {BRANCH_CONFIG[bId].label}
                </span>
                
                {/* Swimlane Column Plus button */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddBlock(bId, true);
                  }}
                  className="mt-2.5 mx-auto w-7 h-7 bg-white dark:bg-slate-800 border border-aero-200 hover:border-aero-400 hover:shadow-sm rounded-full flex items-center justify-center pointer-events-auto text-aero-500 hover:text-aero-700 transition-all active:scale-95"
                  title={`Add block to ${BRANCH_CONFIG[bId].label}`}
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {/* DRAGGABLE BLOCKS LAYER */}
          {(pathway.blocks || []).map((block) => {
            const x = localCoords[block.id]?.x ?? block.x ?? 0;
            const y = localCoords[block.id]?.y ?? block.y ?? 0;
            const isDragging = draggedNodeId === block.id;

            return (
              <div
                key={block.id}
                style={{
                  left: `${x}px`,
                  top: `${y}px`,
                  position: 'absolute',
                  zIndex: isDragging ? 50 : 10
                }}
                className={`w-[208px] bg-white dark:bg-slate-800 border rounded-xl shadow-sm transition-shadow ${
                  isDragging ? 'shadow-lg border-sky-400 ring-2 ring-sky-100 dark:ring-sky-950' : 'border-aero-200 hover:border-aero-400 hover:shadow'
                }`}
              >
                {/* Node Header (Drag Area) */}
                <div
                  onPointerDown={(e) => handleNodeDragStart(e, block.id, x, y)}
                  onPointerMove={(e) => handleNodeDragMove(e, block.id)}
                  onPointerUp={(e) => handleNodeDragEnd(e, block.id)}
                  className="px-3 py-2 bg-slate-50 dark:bg-slate-850 border-b border-aero-100 dark:border-slate-700 rounded-t-xl cursor-grab active:cursor-grabbing flex items-center justify-between"
                >
                  <div className="flex items-center gap-1.5 min-w-0">
                    {renderBlockIcon(block.type)}
                    <span className="text-[9px] font-bold text-aero-400 dark:text-slate-350 uppercase tracking-wide truncate">
                      {getBlockTypeDisplayName(block.type)}
                    </span>
                  </div>
                  
                  {/* Delete button */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteBlock(block.id);
                    }}
                    className="p-1 hover:bg-red-50 dark:hover:bg-red-950/20 text-slate-300 hover:text-red-500 rounded transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* Node Body */}
                <div 
                  onClick={() => onEditBlock(block)}
                  className="p-3 cursor-pointer hover:bg-slate-50/50 dark:hover:bg-slate-750 rounded-b-xl flex flex-col gap-1.5"
                >
                  <div className="font-bold text-xs text-aero-900 dark:text-slate-100 leading-snug line-clamp-2">
                    {block.name}
                  </div>
                  
                  {block.description && (
                    <div className="text-[10px] text-aero-400 dark:text-slate-400 line-clamp-1">
                      {block.description}
                    </div>
                  )}

                  {/* Context-specific tags */}
                  {block.type === 'theory' && block.courseTemplateName && (
                    <div className="mt-1 px-1.5 py-0.5 bg-blue-50 dark:bg-blue-950/20 rounded border border-blue-100 dark:border-blue-900/50 text-[9px] text-blue-600 dark:text-blue-400 font-semibold truncate">
                      {block.courseTemplateName}
                    </div>
                  )}

                  {block.type === 'ojt' && block.ojtConfig?.agentEnabled && (
                    <div className="mt-1 px-1.5 py-0.5 bg-green-50 dark:bg-green-950/20 rounded border border-green-100 dark:border-green-900/50 text-[9px] text-green-600 dark:text-green-400 font-semibold flex items-center gap-1">
                      <Sparkles className="w-3 h-3 text-green-500" />
                      Agent Pilot Active
                    </div>
                  )}

                  {block.type === 'pathway' && block.linkedPathwayId && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenSubPathway(block);
                      }}
                      className="mt-1 py-1 w-full text-center bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-650 text-[9px] font-bold text-slate-600 dark:text-slate-300 rounded border border-slate-200 dark:border-slate-600 transition-colors"
                    >
                      Inspect Sub-Pathway
                    </button>
                  )}
                </div>

                {/* Input Connection Handle Dot */}
                <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-slate-250 dark:bg-slate-700 border-2 border-white dark:border-slate-800 rounded-full pointer-events-none" />
                
                {/* Output Connection Handle Dot */}
                <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-slate-250 dark:bg-slate-700 border-2 border-white dark:border-slate-800 rounded-full pointer-events-none" />
              </div>
            );
          })}
        </div>
      </div>

      {/* FLOAT CANVAS TOOLBAR */}
      <div className="absolute bottom-4 left-4 flex items-center gap-2.5 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border border-aero-200 dark:border-slate-800 px-3.5 py-2 rounded-xl shadow-lg">
        <button 
          onClick={() => setZoomScale(prev => Math.min(1.5, prev + 0.1))}
          className="w-7 h-7 bg-slate-50 hover:bg-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-lg flex items-center justify-center font-bold text-sm text-slate-700 dark:text-slate-300"
          title="Zoom In"
        >
          +
        </button>
        <button 
          onClick={() => setZoomScale(prev => Math.max(0.5, prev - 0.1))}
          className="w-7 h-7 bg-slate-50 hover:bg-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-lg flex items-center justify-center font-bold text-sm text-slate-700 dark:text-slate-300"
          title="Zoom Out"
        >
          -
        </button>
        <button 
          onClick={() => {
            setZoomScale(0.95);
            setPanOffset({ x: 50, y: 50 });
          }}
          className="px-2.5 py-1 bg-slate-50 hover:bg-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-lg text-xs font-semibold text-slate-600 dark:text-slate-300"
          title="Recenter"
        >
          Recenter
        </button>
        <div className="h-4 w-px bg-slate-200 dark:bg-slate-700 mx-1" />
        <span className="text-[10px] text-slate-400 font-medium">Zoom: {Math.round(zoomScale * 100)}%</span>
      </div>
    </div>
  );
};
